import { Alert, Box, Button } from '@mui/material';
import { useEffect, useState } from 'react';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import {
  HedgePlanBudgetTriggerModel,
  HedgePlanSeasonModel,
  HedgePlanTermReportModel,
  HedgePlanTimeTriggerModel,
  HedgePlanTradeModel,
  HedgePlanType,
  HedgePlanValueTriggerModel,
  ListHedgePlanSeasonsResponse,
} from '../../../../Generated/Commodity-Management-Api';
import { StoneXRow, StoneXTab, StoneXTabs } from '../../../StoneX';
import { StoneXButton, StoneXModal, StoneXTextField } from '../../../StoneXMui';
import HedgePlanSeasonTabLayout from './HedgePlanSeasonTabLayout';
import { SeasonPeriod } from './HedgePlanTerms';
import HedgePlanTradesTab from './HedgePlanTradesTab';
import HedgePlanTriggersTab from './HedgePlanTriggersTab';

interface HedgePlanSeasonModalProps {
  open: boolean;
  onClose: () => void;
  hedgePlanId: number;
  planType: HedgePlanType;
  hasBudgetSource: boolean;
  season: HedgePlanSeasonModel;
  period: SeasonPeriod;
  isLastSeason: boolean;
  report?: HedgePlanTermReportModel;
  valueTriggers: HedgePlanValueTriggerModel[];
  budgetTriggers: HedgePlanBudgetTriggerModel[];
  timeTriggers: HedgePlanTimeTriggerModel[];
  trades: HedgePlanTradeModel[];
  enableValueTriggers: boolean;
  enableBudgetTriggers: boolean;
  enableTimeTriggers: boolean;
  onTriggersSaved: (period: SeasonPeriod, valueTriggers: HedgePlanValueTriggerModel[], budgetTriggers: HedgePlanBudgetTriggerModel[], timeTriggers: HedgePlanTimeTriggerModel[]) => void;
  onTradesSaved: (period: SeasonPeriod, trades: HedgePlanTradeModel[]) => void;
  onSaveFailed: (message: string) => void;
  onSeasonsChanged: (seasons: ListHedgePlanSeasonsResponse) => void;
}

type SeasonTab = 'Triggers' | 'Trades' | 'Budget' | 'Settings';

function errorMessage(error: any, fallback: string): string {
  return error?.response?.data?.detail ?? error?.response?.data?.message ?? fallback;
}

function formatVolume(volume: number): string {
  return Math.round(volume).toLocaleString('en-US');
}

function BudgetTab(props: {
  hedgePlanId: number;
  season: HedgePlanSeasonModel;
  report?: HedgePlanTermReportModel;
  onClose: () => void;
  onSeasonsChanged: (seasons: ListHedgePlanSeasonsResponse) => void;
}) {
  const { season } = props;
  const [adjustment, setAdjustment] = useState<string>(season.budgetAdjustment?.toString() ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const budgetedVolume = props.report?.budgetedVolume ?? undefined;
  const parsedAdjustment = adjustment.trim() === '' ? null : Number(adjustment);
  const isNumber = parsedAdjustment === null || !Number.isNaN(parsedAdjustment);
  const adjustedVolume = budgetedVolume !== undefined && isNumber ? budgetedVolume + (parsedAdjustment ?? 0) : undefined;
  const adjustmentPercent = budgetedVolume && isNumber ? ((parsedAdjustment ?? 0) / budgetedVolume) * 100 : undefined;
  const isValid = isNumber && (adjustedVolume === undefined || adjustedVolume > 0);

  async function save() {
    setSaving(true);
    setError(undefined);
    try {
      const response = await CommodityManagementApi.updateHedgePlanSeasonSettings(null, String(props.hedgePlanId), String(season.year), {
        hedgePlanId: props.hedgePlanId,
        year: season.year,
        term: season.term,
        isHidden: season.isHidden,
        budgetAdjustment: parsedAdjustment,
      });
      props.onSeasonsChanged(response.data);
      props.onClose();
    } catch (saveError: any) {
      setError(errorMessage(saveError, 'The budget adjustment could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <HedgePlanSeasonTabLayout
      footer={(
        <StoneXRow justify="end">
          <Button variant="outlined" onClick={props.onClose} disabled={saving}>Cancel</Button>
          <StoneXButton onClick={save} isWorking={saving} disabled={!isValid}>Save</StoneXButton>
        </StoneXRow>
      )}
    >
      <StoneXRow>
        <Box>
          When demand changes after trades are placed, adjust what 100% of this season is instead of changing the trades.
          The trades keep their volume, and the hedge % is measured against the adjusted budget.
        </Box>
      </StoneXRow>
      <StoneXRow>
        <Box sx={{ minWidth: 220 }}>
          <strong>Linked budget:</strong> {budgetedVolume === undefined ? 'Not available until the season runs' : formatVolume(budgetedVolume)}
        </Box>
      </StoneXRow>
      <StoneXRow>
        <StoneXTextField
          label="Adjustment (+ or -)"
          type="number"
          value={adjustment}
          onUpdate={setAdjustment}
          placeholder="e.g. -300000"
          error={!isNumber ? 'Enter a number' : adjustedVolume !== undefined && adjustedVolume <= 0 ? 'The adjusted budget must stay above zero' : undefined}
          width="220px"
        />
      </StoneXRow>
      {adjustedVolume !== undefined && adjustmentPercent !== undefined && (
        <StoneXRow>
          <Box>
            100% is now <strong>{formatVolume(adjustedVolume)}</strong>
            {` (${adjustmentPercent >= 0 ? '+' : ''}${Number(adjustmentPercent.toFixed(2))}% of the budget)`}
          </Box>
        </StoneXRow>
      )}
      {error && (
        <StoneXRow>
          <Alert severity="error" onClose={() => setError(undefined)}>{error}</Alert>
        </StoneXRow>
      )}
    </HedgePlanSeasonTabLayout>
  );
}

function SettingsTab(props: {
  hedgePlanId: number;
  season: HedgePlanSeasonModel;
  isLastSeason: boolean;
  hasTrades: boolean;
  onClose: () => void;
  onSeasonsChanged: (seasons: ListHedgePlanSeasonsResponse) => void;
}) {
  const { season } = props;
  const [working, setWorking] = useState<'hide' | 'delete' | undefined>(undefined);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const canDelete = props.isLastSeason && !season.isCurrent;

  async function toggleHidden() {
    setWorking('hide');
    setError(undefined);
    try {
      const response = await CommodityManagementApi.updateHedgePlanSeasonSettings(null, String(props.hedgePlanId), String(season.year), {
        hedgePlanId: props.hedgePlanId,
        year: season.year,
        term: season.term,
        isHidden: !season.isHidden,
        budgetAdjustment: season.budgetAdjustment ?? null,
      });
      props.onSeasonsChanged(response.data);
      props.onClose();
    } catch (saveError: any) {
      setError(errorMessage(saveError, 'The season could not be updated.'));
    } finally {
      setWorking(undefined);
    }
  }

  async function deleteSeason() {
    setWorking('delete');
    setError(undefined);
    try {
      const response = await CommodityManagementApi.deleteHedgePlanSeason(null, String(props.hedgePlanId), season.year, season.term);
      props.onSeasonsChanged(response.data);
      props.onClose();
    } catch (deleteError: any) {
      setError(errorMessage(deleteError, 'The season could not be deleted.'));
    } finally {
      setWorking(undefined);
      setConfirmingDelete(false);
    }
  }

  return (
    <HedgePlanSeasonTabLayout
      footer={(
        <StoneXRow justify="end">
          <Button variant="outlined" onClick={props.onClose} disabled={!!working}>Close</Button>
        </StoneXRow>
      )}
    >
      <StoneXRow>
        <Box sx={{ flex: 1 }}>
          <strong>{season.isHidden ? 'Unhide this season' : 'Hide this season'}</strong>
          <Box sx={{ color: 'var(--gray4)' }}>
            {season.isHidden
              ? 'Shows the season in the hedges table again.'
              : 'Takes the season out of the hedges table without losing anything. Bring it back from Previous Seasons in the plan settings.'}
          </Box>
        </Box>
        <StoneXButton onClick={toggleHidden} isWorking={working === 'hide'} disabled={!!working}>
          {season.isHidden ? 'Unhide' : 'Hide'}
        </StoneXButton>
      </StoneXRow>

      <StoneXRow>
        <Box sx={{ flex: 1 }}>
          <strong>Delete this season</strong>
          <Box sx={{ color: 'var(--gray4)' }}>
            {!canDelete
              ? 'Only the last season, after the current one, can be deleted.'
              : props.hasTrades
                ? 'This season has trades. Delete them on the Trades tab first.'
                : 'For a season added by mistake. Its triggers are deleted with it.'}
          </Box>
        </Box>
        {!confirmingDelete ? (
          <Button variant="outlined" color="error" onClick={() => setConfirmingDelete(true)} disabled={!canDelete || props.hasTrades || !!working}>Delete</Button>
        ) : (
          <>
            <Button variant="outlined" onClick={() => setConfirmingDelete(false)} disabled={!!working}>Keep</Button>
            <StoneXButton onClick={deleteSeason} isWorking={working === 'delete'} disabled={!!working}>Delete {season.label}</StoneXButton>
          </>
        )}
      </StoneXRow>

      {error && (
        <StoneXRow>
          <Alert severity="error" onClose={() => setError(undefined)}>{error}</Alert>
        </StoneXRow>
      )}

    </HedgePlanSeasonTabLayout>
  );
}

export default function HedgePlanSeasonModal(props: HedgePlanSeasonModalProps) {
  const tabs: SeasonTab[] = ['Triggers', 'Trades', ...(props.hasBudgetSource ? ['Budget' as SeasonTab] : []), 'Settings'];
  const [activeTab, setActiveTab] = useState<SeasonTab>('Triggers');
  const { period } = props;

  useEffect(() => {
    if (props.open) {
      setActiveTab('Triggers');
    }
  }, [props.open, period.key]);

  const seasonTrades = props.trades.filter((trade) => trade.year === period.year && trade.term === period.term);

  return (
    <StoneXModal header={period.label} open={props.open} onClose={props.onClose} width="1100px" height="80vh" sx={{ overflow: 'hidden' }}>
      <StoneXTabs activeTabIndex={tabs.indexOf(activeTab)}>
        {tabs.map((tab, index) => (
          <StoneXTab key={tab} tabIndex={index} isActive={activeTab === tab} onClick={() => setActiveTab(tab)} label={tab} />
        ))}
      </StoneXTabs>

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, paddingTop: 1 }}>
        {activeTab === 'Triggers' && (
          <HedgePlanTriggersTab
            onClose={props.onClose}
            onSaveFailed={props.onSaveFailed}
            onSaved={props.onTriggersSaved}
            hedgePlanId={props.hedgePlanId}
            period={period}
            planType={props.planType}
            valueTriggers={props.valueTriggers}
            budgetTriggers={props.budgetTriggers}
            timeTriggers={props.timeTriggers}
            enableValueTriggers={props.enableValueTriggers}
            enableBudgetTriggers={props.enableBudgetTriggers}
            enableTimeTriggers={props.enableTimeTriggers}
          />
        )}
        {activeTab === 'Trades' && (
          <HedgePlanTradesTab
            onClose={props.onClose}
            onSaveFailed={props.onSaveFailed}
            onSaved={props.onTradesSaved}
            hedgePlanId={props.hedgePlanId}
            period={period}
            trades={props.trades}
            triggers={{ value: props.valueTriggers, budget: props.budgetTriggers, time: props.timeTriggers }}
            byVolume={props.hasBudgetSource}
            reportTrades={props.report?.summary?.trades}
          />
        )}
        {activeTab === 'Budget' && (
          <BudgetTab hedgePlanId={props.hedgePlanId} season={props.season} report={props.report} onClose={props.onClose} onSeasonsChanged={props.onSeasonsChanged} />
        )}
        {activeTab === 'Settings' && (
          <SettingsTab
            hedgePlanId={props.hedgePlanId}
            season={props.season}
            isLastSeason={props.isLastSeason}
            hasTrades={seasonTrades.length > 0}
            onClose={props.onClose}
            onSeasonsChanged={props.onSeasonsChanged}
          />
        )}
      </Box>
    </StoneXModal>
  );
}
